import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Lifebuoy, Pencil, Trash, Plus, MagnifyingGlass, BookOpen,
  ChartLine, Warning, CheckCircle, ChatCircle, Star, Sparkle, ThumbsUp
} from '@phosphor-icons/react';
import { TicketDetailDialog } from './SupportPage';

const PRIORITY_COLORS = {
  urgent: 'bg-red-500/15 text-red-400 border-red-500/30',
  high: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  medium: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  low: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};
const STATUS_COLORS = {
  open: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  in_progress: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  waiting_customer: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  resolved: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  closed: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

function fmtDate(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function shortId(id) { return id ? `#${String(id).slice(-6)}` : ''; }

export default function AdminSupportPage() {
  const { apiCall } = useAuth();
  const [tab, setTab] = useState('tickets');
  const [stats, setStats] = useState(null);

  useEffect(() => { loadStats(); }, []);
  async function loadStats() {
    try { const res = await apiCall('get', '/support/dashboard'); setStats(res.data); } catch (e) { /* silent */ }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-support-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <Lifebuoy size={28} className="text-primary" weight="duotone" />
          Support Inbox
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Manage tickets, knowledge base, and customer satisfaction.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <StatCard icon={ChatCircle} label="Total" value={stats?.total ?? '—'} testid="stat-total" />
        <StatCard icon={Warning} label="Open" value={stats?.open ?? '—'} tone="emerald" testid="stat-open" />
        <StatCard icon={ChartLine} label="In progress" value={stats?.in_progress ?? '—'} tone="blue" testid="stat-in-progress" />
        <StatCard icon={Warning} label="Urgent open" value={stats?.urgent_open ?? '—'} tone="red" testid="stat-urgent" />
        <StatCard
          icon={Star}
          label={`Avg CSAT${stats?.csat_responses ? ` · ${stats.csat_responses}` : ''}`}
          value={stats?.avg_csat ?? '—'}
          tone="amber"
          testid="stat-csat"
        />
      </div>

      {/* AI Deflection row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <StatCard
          icon={Sparkle}
          label="AI chats started"
          value={stats?.ai_sessions_total ?? '—'}
          tone="violet"
          testid="stat-ai-total"
        />
        <StatCard
          icon={ThumbsUp}
          label="Deflected by AI"
          value={stats?.ai_deflected ?? '—'}
          tone="emerald"
          testid="stat-ai-deflected"
        />
        <StatCard
          icon={ChartLine}
          label="Deflection rate"
          value={stats?.ai_deflection_rate != null ? `${stats.ai_deflection_rate}%` : '—'}
          tone="blue"
          testid="stat-ai-deflection-rate"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="tickets" data-testid="admin-tab-tickets">Tickets</TabsTrigger>
          <TabsTrigger value="kb" data-testid="admin-tab-kb">Knowledge Base</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="mt-4">
          <TicketsList apiCall={apiCall} onChange={loadStats} />
        </TabsContent>

        <TabsContent value="kb" className="mt-4">
          <KbAdmin apiCall={apiCall} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testid }) {
  const toneCls = {
    emerald: 'text-emerald-400 bg-emerald-500/10',
    blue: 'text-blue-400 bg-blue-500/10',
    red: 'text-red-400 bg-red-500/10',
    amber: 'text-amber-400 bg-amber-500/10',
    violet: 'text-violet-400 bg-violet-500/10',
  }[tone] || 'text-primary bg-primary/10';
  return (
    <Card data-testid={testid}>
      <CardContent className="py-4 px-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${toneCls}`}>
          <Icon size={18} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="text-xl font-bold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ TICKETS LIST ============
function TicketsList({ apiCall, onChange }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', priority: 'all', category: 'all', search: '' });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.priority, filters.category]);

  async function load() {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filters.status !== 'all') qs.set('status', filters.status);
      if (filters.priority !== 'all') qs.set('priority', filters.priority);
      if (filters.category !== 'all') qs.set('category', filters.category);
      if (filters.search) qs.set('search', filters.search);
      const res = await apiCall('get', `/support/tickets${qs.toString() ? `?${qs.toString()}` : ''}`);
      setTickets(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search subject or message…"
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            className="pl-9"
            data-testid="search-tickets-input"
          />
        </div>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-[150px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'].map(s =>
              <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
          <SelectTrigger className="w-[140px]" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {['urgent', 'high', 'medium', 'low'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
          <SelectTrigger className="w-[150px]" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {['general', 'billing', 'technical', 'account', 'feature_request', 'bug'].map(c =>
              <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading…</div>
      ) : tickets.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <CheckCircle size={48} weight="duotone" className="mx-auto mb-3 text-muted-foreground/40" />
            No tickets match your filters.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => (
            <Card
              key={t.id}
              className="cursor-pointer hover:border-primary/40 transition-colors"
              onClick={() => setActive(t)}
              data-testid={`admin-ticket-row-${t.id}`}
            >
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs text-muted-foreground font-mono">{shortId(t.id)}</span>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[t.status] || ''}`}>{t.status?.replace('_', ' ')}</Badge>
                      <Badge variant="outline" className={`text-[10px] ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</Badge>
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{t.category}</span>
                    </div>
                    <div className="font-medium truncate">{t.subject}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {t.tenant_name || 'Unknown tenant'} · {t.created_by_email} · Updated {fmtDate(t.updated_at)}
                    </div>
                  </div>
                  {t.satisfaction_rating ? (
                    <div className="flex items-center gap-1 text-amber-400 text-sm">
                      <Star size={14} weight="fill" /> {t.satisfaction_rating}
                    </div>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {active && (
        <TicketDetailDialog
          ticketId={active.id}
          onClose={() => { setActive(null); load(); onChange?.(); }}
          apiCall={apiCall}
          isAdmin
        />
      )}
    </div>
  );
}

// ============ KB ADMIN ============
function KbAdmin({ apiCall }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // article or {} for new
  const [confirmDel, setConfirmDel] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function load() {
    setLoading(true);
    try {
      // We use the public KB endpoint which only returns published. For admin, also include unpublished.
      // Backend currently filters is_published=true. We'll keep that but allow admin to manage.
      const res = await apiCall('get', '/support/kb');
      setArticles(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function save(article) {
    try {
      if (article.id) {
        await apiCall('put', `/support/kb/${article.id}`, article);
      } else {
        await apiCall('post', '/support/kb', article);
      }
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  }

  async function remove(id) {
    try {
      await apiCall('delete', `/support/kb/${id}`);
      toast.success('Deleted');
      setConfirmDel(null);
      load();
    } catch (e) { toast.error('Failed to delete'); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <BookOpen size={16} /> {articles.length} article{articles.length !== 1 ? 's' : ''}
        </div>
        <Button onClick={() => setEditing({ title: '', content: '', category: 'general', sort_order: 0, is_published: true })} data-testid="new-kb-btn">
          <Plus size={14} className="mr-1" /> New Article
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">Loading…</div>
      ) : articles.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <BookOpen size={48} weight="duotone" className="mx-auto mb-3 text-muted-foreground/40" />
            No articles yet. Create your first to help customers self-serve.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {articles.map(a => (
            <Card key={a.id}>
              <CardContent className="py-4 px-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{a.category}</div>
                  <div className="font-medium">{a.title}</div>
                  <div className="text-xs text-muted-foreground truncate max-w-md">{a.content?.slice(0, 120)}…</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(a)} data-testid={`edit-kb-${a.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDel(a)} data-testid={`delete-kb-${a.id}`}>
                    <Trash size={14} className="text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <KbEditDialog
          article={editing}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete article?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">"{confirmDel?.title}" will be permanently removed.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => remove(confirmDel.id)} data-testid="confirm-delete-kb">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KbEditDialog({ article, onClose, onSave }) {
  const [form, setForm] = useState({
    title: article.title || '',
    content: article.content || '',
    category: article.category || 'general',
    sort_order: article.sort_order || 0,
    is_published: article.is_published !== false,
  });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{article.id ? 'Edit article' : 'New article'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="kb-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="kb-category-input" />
            </div>
            <div>
              <Label>Sort order</Label>
              <Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: Number(e.target.value || 0) })} />
            </div>
          </div>
          <div>
            <Label>Content</Label>
            <Textarea rows={10} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} data-testid="kb-content-input" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={e => setForm({ ...form, is_published: e.target.checked })}
              data-testid="kb-published-checkbox"
            />
            Published (visible to tenants and AI)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave({ ...article, ...form })} disabled={!form.title.trim() || !form.content.trim()} data-testid="save-kb-btn">
            Save article
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
